// src/components/index.jsx
import { useState, useEffect } from 'react';
import { supabase, API_LIMITS } from '../lib/supabase';

// ============================================================
// RATE LIMIT INDICATOR
// ============================================================
export function RateLimitBanner({ provider }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    fetchUsage();
  }, [provider]);

  async function fetchUsage() {
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from('api_call_log')
      .select('calls_used')
      .eq('api_provider', provider)
      .gte('created_at', since);

    const used = (data || []).reduce((s, r) => s + (r.calls_used || 1), 0);
    const limit = API_LIMITS[provider]?.daily_limit;
    setUsage({ used, limit, pct: limit ? (used / limit) * 100 : 0 });
  }

  if (!usage) return null;
  const cls = usage.pct > 80 ? 'danger' : usage.pct > 50 ? 'warn' : 'safe';

  return (
    <div className="rate-limit-bar">
      <span className="rate-limit-provider">{API_LIMITS[provider]?.name || provider}</span>
      <div className="rate-limit-track">
        <div className="rate-limit-fill" style={{width:`${Math.min(usage.pct,100)}%`}} />
      </div>
      <span className="rate-limit-count" style={{color: cls === 'danger' ? 'var(--red)' : cls === 'warn' ? 'var(--yellow)' : 'var(--text-muted)'}}>
        {usage.used.toLocaleString()} / {usage.limit?.toLocaleString() || '?'} today
      </span>
    </div>
  );
}

// ============================================================
// API USAGE CONFIRMATION MODAL
// ============================================================
export function ApiConfirmModal({ isOpen, onConfirm, onCancel, action, provider, estimatedCalls }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (isOpen && provider) fetchUsage();
  }, [isOpen, provider]);

  async function fetchUsage() {
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase.from('api_call_log').select('calls_used').eq('api_provider', provider).gte('created_at', since);
    const used = (data || []).reduce((s, r) => s + (r.calls_used || 1), 0);
    const limit = API_LIMITS[provider]?.daily_limit;
    setUsage({ used, remaining: limit ? limit - used : '?', limit });
  }

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:400}}>
        <div className="modal-header">
          <span className="modal-title">Confirm API Call</span>
          <button className="btn btn-icon btn-secondary" onClick={onCancel} style={{padding:'4px 8px', fontSize:14}}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{marginBottom:12}}>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:8}}>{action}</div>
            <div className="rate-limit-bar">
              <span className="rate-limit-provider">{API_LIMITS[provider]?.name || provider}</span>
              <div className="rate-limit-track" style={{height:4,flex:1,background:'var(--bg-base)',borderRadius:2}}>
                {usage && <div className="rate-limit-fill" style={{width:`${Math.min((usage.used/usage.limit)*100,100)}%`, height:'100%', borderRadius:2, background: usage.remaining < 50 ? 'var(--red)' : usage.remaining < 100 ? 'var(--yellow)' : 'var(--green)'}} />}
              </div>
            </div>
          </div>
          {usage && (
            <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-sm)',padding:'10px 12px',fontSize:12,fontFamily:'var(--font-mono)'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{color:'var(--text-muted)'}}>Before this call</span>
                <span style={{color:'var(--text-secondary)'}}>{usage.remaining} remaining</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <span style={{color:'var(--text-muted)'}}>After this call (~{estimatedCalls || 1} calls)</span>
                <span style={{color: usage.remaining - (estimatedCalls||1) < 10 ? 'var(--red)' : 'var(--green)'}}>
                  {typeof usage.remaining === 'number' ? usage.remaining - (estimatedCalls||1) : '?'} remaining
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm}>Proceed</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SLEEVE SELECTOR
// ============================================================
export function SleevePicker({ sleeves, activeId, onChange, showAll = true }) {
  return (
    <div className="sleeve-pills">
      {showAll && (
        <button className={`sleeve-pill ${!activeId ? 'active' : ''}`} onClick={() => onChange(null)}>
          All Sleeves
        </button>
      )}
      {sleeves.map(s => (
        <button key={s.id} className={`sleeve-pill ${activeId === s.id ? 'active' : ''}`} onClick={() => onChange(s.id)}>
          {s.name}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// SIMPLE CONFIRM MODAL
// ============================================================
export function ConfirmModal({ isOpen, title, message, onConfirm, onCancel, danger }) {
  if (!isOpen) return null;
  return (
    <div className="modal-backdrop">
      <div className="modal" style={{maxWidth:360}}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
        </div>
        <div className="modal-body">
          <p style={{color:'var(--text-secondary)',fontSize:13,lineHeight:1.6}}>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LOADING OVERLAY
// ============================================================
export function LoadingOverlay({ message }) {
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:40,gap:12}}>
      <div className="spinner" />
      {message && <div style={{fontSize:13,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>{message}</div>}
    </div>
  );
}

// ============================================================
// COLOR HELPERS
// ============================================================
export function fmtPct(v, digits=2) {
  if (v == null || isNaN(v)) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
}
export function fmtNum(v, digits=2, prefix='') {
  if (v == null || isNaN(v)) return '—';
  return `${prefix}${v.toLocaleString(undefined, {minimumFractionDigits: digits, maximumFractionDigits: digits})}`;
}
export function fmtMoney(v, digits=0) {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}$${(abs/1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(2)}M`;
  if (abs >= 1e3) {
    const rounded = Math.round(abs / 10) * 10;
    return `${sign}$${(rounded / 1e3).toFixed(2)}K`;
  }
  return `${sign}$${abs.toFixed(digits)}`;
}
export function colorClass(v, invertSign = false) {
  if (v == null || isNaN(v)) return '';
  const positive = invertSign ? v < 0 : v > 0;
  return positive ? 'positive' : v === 0 ? '' : 'negative';
}
